/**
 * TopProducts — what actually sold.
 *
 * Magnitude, ranked, with long names: horizontal bars in ONE hue, not the
 * categorical set. These items are not identities to tell apart, they are
 * quantities to compare, and painting each a different colour would imply a
 * relationship between them that does not exist.
 *
 * Bars are thin and the value is direct-labelled, so the row reads without an
 * axis to measure against.
 */
import { Card } from '@/components/ui/Card';
import { Qty } from '@/components/ui/numeric';
import { SEQ_HUE } from './chartTokens';

export interface TopProduct {
  productId: string;
  name: string;
  qty: number;
  unit: string | null;
}

export function TopProducts({ items }: { items: TopProduct[] }) {
  const max = Math.max(...items.map((i) => i.qty), 1);

  return (
    <Card className="p-5">
      <h2 className="text-heading font-medium text-fg-primary">Top selling products</h2>
      <p className="mb-4 text-label text-fg-tertiary">
        {items.length > 0 ? 'By quantity sold, last 7 days' : 'No sales in the last 7 days'}
      </p>

      {items.length === 0 ? (
        <p className="text-body text-fg-tertiary">Ring up a sale and it will show here.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((p) => (
            <li key={p.productId}>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <span className="truncate text-body text-fg-primary">{p.name}</span>
                <Qty value={p.qty} unit={p.unit} className="shrink-0 text-body text-fg-secondary" />
              </div>
              {/* 4px rounded end, anchored to the baseline. */}
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${(p.qty / max) * 100}%`, backgroundColor: SEQ_HUE }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
